import React from 'react';
import {
  getCrmContactDisplayPhotoUrl,
  getCrmCompanyLogoDisplayUrl,
  getCrmTeamMemberDisplayPhotoUrl,
} from '../../services/firestoreService';

const AVATAR = 'w-[46px] h-[46px]';

/** Round avatar with photo → initials fallback. */
const Avatar = ({ photoUrl, initials }) => (
  <div className={`${AVATAR} shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-500 flex items-center justify-center`}>
    {photoUrl ? (
      <img
        src={photoUrl}
        alt=""
        className={`${AVATAR} object-cover`}
        onError={(e) => {
          e.target.style.display = 'none';
          const next = e.target.nextSibling;
          if (next) next.style.display = 'flex';
        }}
      />
    ) : null}
    <div className={`${AVATAR} bg-gray-400 rounded-full items-center justify-center text-white font-semibold text-lg ${photoUrl ? 'hidden' : 'flex'}`}>
      {String(initials || '?').toUpperCase()}
    </div>
  </div>
);

/**
 * Contact/team-member avatars + company logo, plus (optionally) a names line —
 * the shared avatar cluster used on the CRM HR Fulfillment Kanban cards and the
 * request overview. `teamMembers` are portal users linked to the account
 * (primary sorted first).
 *
 * Shows at most two members' pictures (the first two). When there are more than
 * two members, the names line collapses to first names only. Pass
 * `showNames={false}` to render just the avatars.
 */
const HiringAvatarCluster = ({ account, company, teamMembers = [], showNames = true }) => {
  const logoUrl = account ? getCrmCompanyLogoDisplayUrl(account) : '';
  const primaryPhoto = account ? getCrmContactDisplayPhotoUrl(account) : '';
  const primaryInitials =
    (`${account?.contact?.firstName?.[0] || ''}${account?.contact?.lastName?.[0] || ''}`.trim()) ||
    (company ? company.trim()[0] : '?');

  const memberInitials = (m) =>
    `${m.firstName?.[0] || ''}${m.lastName?.[0] || ''}`.trim() ||
    (m.name ? m.name.trim()[0] : '?');

  // Primary contact photo counts as the first member picture; show at most one
  // additional member avatar so no card exceeds two pictures.
  const extraToShow = teamMembers.filter((m) => !m.isPrimary).slice(0, 1);

  // Names line: full names normally; first names only when >2 team members.
  const count = teamMembers.length;
  const firstNamesOnly = count > 2;
  const displayName = (m) =>
    firstNamesOnly ? (m.firstName || String(m.name || '').split(' ')[0]) : m.name;
  const primaryContactName = `${account?.contact?.firstName || ''} ${account?.contact?.lastName || ''}`.trim();
  const namesLine = count > 0
    ? teamMembers.map(displayName).filter(Boolean).join(', ')
    : primaryContactName;

  return (
    <>
      <div className="flex flex-row flex-wrap items-center justify-center gap-1 mb-1.5">
        <Avatar photoUrl={primaryPhoto} initials={primaryInitials} />
        {logoUrl ? (
          <div className={`${AVATAR} shrink-0 rounded-full overflow-hidden bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center`}>
            <img src={logoUrl} alt="" className="max-w-[88%] max-h-[88%] object-contain" />
          </div>
        ) : null}
        {extraToShow.map((m) => (
          <Avatar
            key={m.uid}
            photoUrl={getCrmTeamMemberDisplayPhotoUrl(account, m.uid) || m.profileImage || ''}
            initials={memberInitials(m)}
          />
        ))}
      </div>
      {showNames && namesLine && (
        <p className="w-full text-center text-sm text-gray-500 dark:text-gray-400 break-words [overflow-wrap:anywhere]">
          {namesLine}
        </p>
      )}
    </>
  );
};

export default HiringAvatarCluster;
